// @workflow assistant.voice
/**
 * Recording rules for dictation (contract "Dictation"): which type to record, the server's
 * limits, the near-limit warning, the clock, and the level meter's numbers. The limits always
 * come from GET api/assistant/transcriptions/limits; nothing here hard-codes them.
 * No Vue or path-alias imports, so `npm run test:unit` (node --test) can load it directly.
 */

/** In order of preference. Never Ogg (Firefox's default when no type is given): the server refuses it. */
export const RECORDING_TYPES = Object.freeze(['audio/webm;codecs=opus', 'audio/mp4']);

/** The time turns amber and says "10 s left" this many seconds before maxSeconds. */
export const NEAR_LIMIT_SECONDS = 10;

/** Level-meter bar heights in px (the showcase's .dict-meter is 24 px tall). */
export const METER_BAR = Object.freeze({ MIN_PX: 3, MAX_PX: 22 });
/** Speech rarely fills the full sample range; this makes normal speech reach most of the meter. */
const LEVEL_GAIN = 4;
const SAMPLE_MIDPOINT = 128;

/**
 * Whether the site's Permissions-Policy lets this page use the mic. `policy` is
 * document.permissionsPolicy or document.featurePolicy (Chrome, Edge); without one (Firefox,
 * Safari) the answer is yes and getUserMedia decides.
 */
export function micAllowedBySitePolicy(policy) {
  if (!policy || typeof policy.allowsFeature !== 'function') return true;
  try {
    return policy.allowsFeature('microphone') !== false;
  } catch (err) {
    return true;
  }
}

export const SUPPORT = Object.freeze({ INSECURE: 'insecure', UNSUPPORTED: 'unsupported' });

/** 'audio/webm;codecs=opus' → 'audio/webm'. */
export const baseType = (mime) => String(mime || '').split(';')[0].trim().toLowerCase();

const supports = (isTypeSupported, type) => {
  try {
    return !!isTypeSupported(type);
  } catch (err) {
    return false;
  }
};

/**
 * The first recording type the browser supports and, when the server listed its types, the
 * server accepts too. Null when there is none.
 * @param {(type: string) => boolean} isTypeSupported MediaRecorder.isTypeSupported
 * @param {string[]|null} [accepted] the limits' mimeTypes (base types)
 */
export function pickRecordingType(isTypeSupported, accepted = null) {
  const allowed = accepted ? accepted.map(baseType) : null;
  return RECORDING_TYPES.find((type) => supports(isTypeSupported, type) && (!allowed || allowed.includes(baseType(type)))) || null;
}

const positive = (n) => typeof n === 'number' && Number.isFinite(n) && n > 0;

/**
 * The limits endpoint's body → `{ maxSeconds, bitsPerSecond, mimeTypes }`, or null when it is not
 * usable. `mimeTypes` is null when the server listed none (then only the browser decides).
 */
export function readLimits(body) {
  if (!body || !positive(body.maxSeconds) || !positive(body.recommendedBitsPerSecond)) return null;
  const types = Array.isArray(body.mimeTypes) ? body.mimeTypes.filter((t) => typeof t === 'string' && t.trim()) : [];
  return { maxSeconds: body.maxSeconds, bitsPerSecond: body.recommendedBitsPerSecond, mimeTypes: types.length ? types : null };
}

/** MediaRecorder options from the limits and the picked type. */
export const recorderOptions = (limits, mimeType) => ({ mimeType, audioBitsPerSecond: limits.bitsPerSecond });

export const isNearLimit = (elapsed, maxSeconds) => elapsed >= maxSeconds - NEAR_LIMIT_SECONDS;
export const secondsLeft = (elapsed, maxSeconds) => Math.max(0, Math.ceil(maxSeconds - elapsed));

/** Seconds → 'm:ss'. */
export function clock(seconds) {
  const whole = Math.max(0, Math.floor(seconds));
  return `${Math.floor(whole / 60)}:${String(whole % 60).padStart(2, '0')}`;
}

/** Time-domain samples (AnalyserNode.getByteTimeDomainData) → loudness 0..1 (RMS, with gain). */
export function levelFromSamples(samples) {
  if (!samples || !samples.length) return 0;
  let sum = 0;
  for (let i = 0; i < samples.length; i += 1) {
    const v = (samples[i] - SAMPLE_MIDPOINT) / SAMPLE_MIDPOINT;
    sum += v * v;
  }
  return Math.min(1, Math.sqrt(sum / samples.length) * LEVEL_GAIN);
}

/** Loudness 0..1 → a meter bar height in px. */
export function barHeight(level) {
  const clamped = Math.min(1, Math.max(0, Number(level) || 0));
  return Math.round(METER_BAR.MIN_PX + clamped * (METER_BAR.MAX_PX - METER_BAR.MIN_PX));
}

/**
 * Can this page dictate at all? The mic needs a secure page (HTTPS or localhost) and
 * MediaRecorder with a type from RECORDING_TYPES.
 * @returns {{ ok: boolean, reason: string|null }}
 */
export function dictationSupport({ isSecureContext, mediaDevices, MediaRecorder } = {}) {
  if (!isSecureContext || !mediaDevices || typeof mediaDevices.getUserMedia !== 'function') return { ok: false, reason: SUPPORT.INSECURE };
  if (!MediaRecorder || typeof MediaRecorder.isTypeSupported !== 'function' || !pickRecordingType((t) => MediaRecorder.isTypeSupported(t))) {
    return { ok: false, reason: SUPPORT.UNSUPPORTED };
  }
  return { ok: true, reason: null };
}
